import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import FigoLoadingOverlay from '../../common/FigoLoadingOverlay';

import { getCoveragePeriodRequest, getOrcaCoveragePreview, getProxyPolicy, saveOrcaQuote } from '../../../actions';
import { PolicyHeader } from '../PolicyHeader';
import { CHANGE_COVERAGE_CHECKOUT, INSURANCE } from '../../navigation/routes.constants';
import { useChangeCoverageOrcaCa } from './hooks/useChangeCoverageOrcaCa';
import { currencyFormat, possesiveAdaptationI18n } from '../../../services/utils';
import { formatShortDateByLanguage } from '../../../services/language.utils';

import { ChangeCoverageButtons } from './ChangeCoverageButtons';
import { BasePlanCard } from './changeCoverageOrcaOptions/BasePlanCard';
import { LimitSelector } from './changeCoverageOrcaOptions/LimitSelector';
import { WellnessOptionCard } from './changeCoverageOrcaOptions/WellnessOptionCard';

import infoIcon from '../../../assets/changeCoverage/info-deep-blue.svg';

import { ORCA_CHANGE_COVERAGE_PRODUCT_CODE } from '../../../constants';

import styles from './css/ChangeCoverageOrcaOptions.module.css';
import { getOrcaPolicyCodeByName } from './utils/orcaChangeCoverage.utils';
import { useChangeCoverageOrcaEffect } from './hooks/useChangeCoverageOrcaEffect';
import { useUpToTop } from '../../../hooks';

function matchById(list, id, fallbackId) {
  return list.find((value) => value.id === id)?.id ?? fallbackId;
}

function getValueById(list, id, fallbackValue) {
  return list.find((value) => value.id === value)?.value ?? fallbackValue;
}

export const ChangeCoverageOrcaOptions = () => {
  const { t } = useTranslation('changeCoverage');
  const dispatch = useDispatch();
  const history = useHistory();

  const [selectedProductCode, setSelectedProductCode] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState(0);
  const [initialLimitsByProduct, setInitialLimitsByProduct] = useState(null);
  const [selectedLimitsByProduct, setSelectedLimitsByProduct] = useState({});
  const [wellnessSelection, setWellnessSelection] = useState(null);
  const [
    wellnessDefaultSelection,
    setWellnessDefaultSelection,
  ] = useState(null);
  const [undoWellnessRemoved, setUndoWellnessRemoved] = useState(false);

  const {
    baseProducts,
    calculatePrice,
    coveragePeriodId,
    currentWellness,
    defaultPolicy,
    effectiveOn,
    initialSelectedLimits,
    loading,
    marketingChanelId,
    paymentManagementUrl,
    petId,
    petName,
    policyId,
    productName,
    queryPetId,
    quotePreview,
    quoteStatusDisplay,
    selectedPet,
    wellnessOptions,
  } = useChangeCoverageOrcaCa(selectedProductCode);

  useChangeCoverageOrcaEffect();

  const [selectedPlan, setSelectedPlan] = useState(productName);

  const accidentOnlyProduct = useMemo(() => {
    const { accidentOnly } = ORCA_CHANGE_COVERAGE_PRODUCT_CODE;
    if (!selectedLimitsByProduct[accidentOnly]) {
      return {};
    }

    return { ...selectedLimitsByProduct[accidentOnly] };
  }, [selectedLimitsByProduct]);

  const accidentIllnessProduct = useMemo(() => {
    const { accidentIllness } = ORCA_CHANGE_COVERAGE_PRODUCT_CODE;

    if (!selectedLimitsByProduct[accidentIllness]) {
      return {};
    }

    return { ...selectedLimitsByProduct[accidentIllness] };
  }, [selectedLimitsByProduct]);

  const onChangeCoverageClick = () => {
    const data = {
      coveragePeriodId,
      internalPetId: selectedPet.petId,
      selectedLimits: selectedLimitsByProduct[selectedProductCode],
      selectedProductCode,
      selectedProducts: [{
        stateFilingProductId: selectedProductId,
      }],
      selectedWellness: wellnessSelection,
    };

    if (wellnessSelection?.code) {
      data.selectedProducts.push({
        stateFilingProductId: wellnessSelection.stateFilingProductId,
      });
    }

    dispatch(saveOrcaQuote(data));
  };

  const onKeepClick = () => {
    history.goBack();
  };

  const selectedProduct = useMemo(() => baseProducts
    ?.find(({
      code,
    }) => code === selectedProductCode), [baseProducts, selectedProductCode]);

  const selectedLimits = selectedLimitsByProduct[selectedProductCode];
  const initialLimits = initialLimitsByProduct?.[selectedProductCode];

  const handleSelectProduct = (product) => () => {
    setSelectedProductCode(product.code);
    setSelectedPlan(product.display);
    setSelectedProductId(product.stateFilingProductId);
  };

  const handleLimitChange = (dimensionKey) => (option) => {
    setSelectedLimitsByProduct((prev) => ({
      ...prev,
      [selectedProductCode]: {
        ...prev[selectedProductCode],
        [dimensionKey]: option.id,
      },
    }));
  };

  function handleSelectWellness(option) {
    return () => setWellnessSelection(option);
  }

  const onUndoWellness = () => {
    setWellnessSelection(wellnessDefaultSelection);
    setUndoWellnessRemoved(false);
  };

  const handleRemoveWellness = () => {
    setWellnessSelection(null);
    setUndoWellnessRemoved(true);
  };

  const formatDate = (date) => (!date ? '-' : formatShortDateByLanguage(date));

  const calculateProductPrice = useCallback((product) => {
    const quote = quotePreview[product.code];
    const monthlyPreviewResults = quote?.monthlyPreviewResults || [];

    return calculatePrice({
      monthlyPreviewResults,
      productSelected: product || {},
      wellnessSelected: wellnessSelection || {},
    });
  }, [quotePreview, selectedProductId, wellnessSelection]);

  useUpToTop();

  useLayoutEffect(() => {
    if (!queryPetId) {
      history.replace(INSURANCE);
    }
  }, [queryPetId]);

  useEffect(() => {
    if (quoteStatusDisplay === 'Ready To Checkout') {
      const url = `${CHANGE_COVERAGE_CHECKOUT}?`
        .replace(':marketingChanelId', marketingChanelId)
        .replace(':coveragePeriodId', coveragePeriodId);

      history.push(url, { coveragePeriodId });
    }
  }, [quoteStatusDisplay]);

  useEffect(() => {
    if (accidentOnlyProduct.stateFilingProductId) {
      dispatch(getOrcaCoveragePreview({
        accidentIllnessProduct,
        accidentOnlyProduct,
        paymentManagementUrl,
        petId,
      }));
    }
  }, [
    accidentOnlyProduct,
    accidentIllnessProduct,
    petId,
    paymentManagementUrl,
  ]);

  useEffect(() => {
    if (policyId && defaultPolicy?.Id) {
      dispatch(getCoveragePeriodRequest({
        coveragePeriodId,
        effectiveOn: defaultPolicy.PolicyEndDate,
        managementUrl: paymentManagementUrl,
      }));

      dispatch(getProxyPolicy({
        managementUrl: paymentManagementUrl,
        policyId,
      }));
    }
  }, [policyId, paymentManagementUrl, defaultPolicy]);

  useEffect(() => {
    if (loading || !baseProducts?.length || initialLimitsByProduct) {
      return;
    }

    const productCode = getOrcaPolicyCodeByName(productName);
    const activeProduct = baseProducts
      .find(({ code }) => code === productCode) || baseProducts[0];

    if (!activeProduct.code) {
      return;
    }

    const seeded = baseProducts.reduce((acc, product) => {
      const { limits: productLimits } = product;

      acc[product.code] = {
        coinsurance: getValueById(
          productLimits.coinsuranceLimits,
          initialSelectedLimits.coinsuranceId,
          productLimits.coinsuranceLimits[0]?.value,
        ),
        coinsuranceId: matchById(
          productLimits.coinsuranceLimits,
          initialSelectedLimits.coinsuranceId,
          productLimits.coinsuranceLimits[0]?.id,
        ),
        coverage: getValueById(
          productLimits.coverageLimits,
          initialSelectedLimits.coverageLimitId,
          productLimits.coverageLimits[0]?.value,
        ),
        coverageLimitId: matchById(
          productLimits.coverageLimits,
          initialSelectedLimits.coverageLimitId,
          productLimits.coverageLimits[0]?.id,
        ),
        deductible: getValueById(
          productLimits.deductibleLimits,
          initialSelectedLimits.deductibleId,
          productLimits.deductibleLimits[0]?.value,
        ),
        deductibleId: matchById(
          productLimits.deductibleLimits,
          initialSelectedLimits.deductibleId,
          productLimits.deductibleLimits[0]?.id,
        ),
        incident: getValueById(
          productLimits.incidentLimits,
          initialSelectedLimits.incidentLimitId,
          productLimits.incidentLimits[0]?.value,
        ),
        incidentLimitId: matchById(
          productLimits.incidentLimits,
          initialSelectedLimits.incidentLimitId,
          productLimits.incidentLimits[0]?.id,
        ),
        selectedProductCode: product.code,
        stateFilingProductId: product.stateFilingProductId,
      };

      return acc;
    }, {});

    setInitialLimitsByProduct(seeded);
    setSelectedLimitsByProduct(seeded);
    setSelectedProductCode(productCode);
    setSelectedProductId(activeProduct.stateFilingProductId);
    setWellnessSelection(currentWellness);
    setWellnessDefaultSelection(currentWellness);
  }, [
    baseProducts,
    currentWellness,
    initialLimitsByProduct,
    initialSelectedLimits,
    loading,
    productName,
    selectedProductCode,
  ]);

  return (
    <div className="Change-coverage-insurance-screen-container">
      <h1>{t('options.optionsTitle')}</h1>

      <div className={`Colors-${marketingChanelId}`}>
        <PolicyHeader
          company={+marketingChanelId}
          logoClass="Change-coverage-header-logo"
        />
      </div>

      {selectedProduct && (
        <div className={styles.optionsContainer}>
          <p className={styles.optionsDescription}>
            {t('options.orca.reviewBasePlan')}
            {' '}

            <strong>
              {t('options.orca.reviewBasePlanEffective', {
                effDate: formatDate(effectiveOn),
                interpolation: { escapeValue: false },
              })}
            </strong>
          </p>

          <div className={styles.planCards}>
            {baseProducts?.length
              && baseProducts.map((product) => {
                const value = calculateProductPrice(product);

                return (
                  <BasePlanCard
                    key={product.productId}
                    code={product.code}
                    disabled={selectedPlan === product?.display}
                    onSelect={handleSelectProduct(product)}
                    price={value && currencyFormat({ value })}
                    selected={product.code === selectedProductCode}
                    title={product.display}
                  />
                );
              })}
          </div>

          {selectedLimits && initialLimits && (
            <div className={styles.customizeContainer}>
              <h3>{t('options.orca.customizePlan')}</h3>

              <p className={styles.optionsDescription}>
                {t('options.orca.customizeInstruction', {
                  count: possesiveAdaptationI18n(petName),
                  name: petName,
                })}
              </p>

              <LimitSelector
                onChange={handleLimitChange('coverageLimitId')}
                options={selectedProduct.limits.coverageLimits}
                selectedId={selectedLimits.coverageLimitId}
                title={t('options.orca.annualLimit')}
                tooltip={t('tooltip.annualCoverageTooltip')}
              />

              <LimitSelector
                ignoreDisabled
                onChange={handleLimitChange('deductibleId')}
                options={selectedProduct.limits.deductibleLimits}
                selectedId={selectedLimits.deductibleId}
                title={t('sliders.deductibleTitle')}
                tooltip={t('tooltip.deductibleTooltip')}
              />

              <LimitSelector
                onChange={handleLimitChange('coinsuranceId')}
                options={selectedProduct.limits.coinsuranceLimits}
                selectedId={selectedLimits.coinsuranceId}
                title={t('sliders.reimbursementTitle')}
                tooltip={t('tooltip.reimbursementTooltip')}
              />
            </div>
          )}

          {undoWellnessRemoved && (
            <div className={styles.wellnessUndo}>
              <img alt="" src={infoIcon} />

              <span>{t('options.orca.wellnessUndo')}</span>

              <button
                className="Like-span"
                onClick={onUndoWellness}
                type="button"
              >
                {t('options.orca.wellnessUndoButton')}
              </button>
            </div>
          )}

          {!!wellnessOptions?.length && !undoWellnessRemoved && (
            <div className={styles.wellnessContainer}>
              <div className={styles.wellnessHeader}>
                <h3>{t('options.orca.optionalWellness')}</h3>

                {wellnessSelection && (
                  <button
                    className={styles.removeWellness}
                    onClick={handleRemoveWellness}
                    type="button"
                  >
                    {t('options.orca.removeWellnessCoverage')}
                  </button>
                )}
              </div>

              <p className={styles.optionsDescription}>
                {t('options.orca.optionalWellnessInstruction')}
              </p>

              <div className={styles.wellnessCards}>
                {wellnessOptions.map((option) => (
                  <WellnessOptionCard
                    key={option.productId}
                    current={option.code === currentWellness?.code}
                    details={option.details}
                    onSelect={handleSelectWellness(option)}
                    price={option.price}
                    selected={option.code === wellnessSelection?.code}
                    title={option.display}
                  />
                ))}
              </div>
            </div>
          )}

          <div className={styles.optionsButtons}>
            <ChangeCoverageButtons
              onClickPrimaryButton={onChangeCoverageClick}
              onClickSecondaryButton={onKeepClick}
              primaryButtonColor="var(--pc-color-blue-300)"
              primaryText={t('options.reviewChanges')}
              secondaryText={t('preview.back')}
            />
          </div>
        </div>
      )}

      <FigoLoadingOverlay
        visible={loading || !selectedProductId}
      />
    </div>
  );
};
